import type {
  CombinedError,
  Operation} from '@urql/vue';
import {
  createClient,
  errorExchange,
  fetchExchange,
  ssrExchange,
} from '@urql/vue';
import { devtoolsExchange } from '@urql/devtools';

import { AgentLoggedDocument } from '../generated/graphql';
import cacheExchange from './cache';

import keyRoutesEnum from '@/router/keyRoutesEnum';

/* All 401 response will be redirect to login page except useAgentLoggedQuery query  */
const onError = (error: CombinedError, operation: Operation) => {
  const operationBody = operation.query.loc?.source.body ?? '';
  const isGetLogged = AgentLoggedDocument.loc?.source.body === operationBody;
  // IntrospectionQuery should not be send
  // https://formidable.com/open-source/urql/docs/graphcache/schema-awareness/#integrating-a-schema
  const isIntrospectionQuery = operationBody.search('query IntrospectionQuery') >= 0;
  if (!isIntrospectionQuery && !isGetLogged && error.response?.status === 401) {
    const currentLocation = btoa(window.location.href);
    // Reload web page to remove all URQL cache
    const url = `${keyRoutesEnum.login}?redirect=${currentLocation}`;
    window.location.href = url;
  }
};

const getCookieValue = (name: string) => {
  const result = document.cookie.match(`(^|[^;]+)\\s*${name}\\s*=\\s*([^;]+)`);
  const value = result?.pop() ?? '';
  return decodeURIComponent(value);
};

export const ssr = ssrExchange({ isClient: import.meta.client });

const getClient = (domain: string) => createClient({
  url: `${domain}/v2/graphql/agent`,
  preferGetMethod: false,
  fetchOptions: () => {
    const xsrf = getCookieValue('XSRF-TOKEN');
    return {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        // Do not set Content-Type: urql sets application/json for string bodies and
        // must omit it for multipart/form-data (e.g. file uploads) so the boundary is set.
        Cache: 'no-cache',
        'X-XSRF-TOKEN': xsrf,
      },
      credentials: 'include',
    };
  },
  exchanges: [
    devtoolsExchange,
    cacheExchange,
    errorExchange({ onError }),
    ssr, // Add `ssr` in front of the `fetchExchange`
    fetchExchange,
  ],
});

export default getClient;
